import React, { forwardRef } from "react";
import logoWhite from "../../assets/habitia-logo-white.png";
import "./presupuestoExpressTemplate.css";

/**
 * Hoja A4 de 1 carilla — "Presupuesto exprés" para dejar en el edificio (cartas).
 * Se renderiza offscreen y se captura con html2canvas → PDF de 1 hoja.
 * Todos los datos entran por `data`; hay defaults para que renderice igual.
 */
const BENEFICIOS_DEFAULT = [
  { t: "Todo a la vista.", d: "Cada gasto, pago y saldo del edificio en una app clara — sin planillas ni sorpresas." },
  { t: "Cobranza al día.", d: "Recordatorios automáticos de expensas y menos morosidad." },
  { t: "App para los vecinos.", d: "Expensas, reservas, incidencias y avisos, todo en el celular." },
  { t: "Cuentas claras.", d: "Estado de cuenta en vivo y conciliación bancaria automática." },
  { t: "Documentos y asambleas.", d: "Actas, reglamentos y votaciones — todo online y ordenado." },
  { t: "Respuesta de verdad.", d: "Atención directa + Habi, el asistente que responde 24/7." },
];

const LEAD_DEFAULT =
  "<b>Habitia</b> es la plataforma uruguaya para administrar tu edificio: transparente, en una app y con soporte de verdad. Preparamos esta propuesta <b>a medida para su edificio</b>.";

const PresupuestoExpressTemplate = forwardRef(function PresupuestoExpressTemplate({ data = {} }, ref) {
  const {
    edificio = "Su edificio",
    direccion = "",
    lead = LEAD_DEFAULT,
    beneficios = BENEFICIOS_DEFAULT,
    honorario = "",
    incluye = "administración integral, app para residentes, cobranzas, conciliación bancaria, liquidación de sueldos del personal y soporte directo.",
    ctaTitle = "¿Les interesa? Coordinemos una reunión sin compromiso — les mostramos la plataforma en 20 minutos.",
    comercial = "",
    wa = "",
    email = "",
    web = "habitia.ai",
    qrDataUrl = "",
    nro = "",
    fecha = "",
  } = data;

  return (
    <div className="pe-page" ref={ref}>
      <header className="pe-hero">
        <img className="pe-logo" src={logoWhite} alt="Habitia" />
        <div className="pe-head-r">
          <div className="pe-kicker">Propuesta de administración</div>
          <div className="pe-web">{web}</div>
        </div>
      </header>

      <main className="pe-body">
        <div className="pe-h1">
          Para la Comisión de
          <b>{edificio}</b>
        </div>
        {direccion ? <div className="pe-addr">{direccion}</div> : null}
        <p className="pe-lead" dangerouslySetInnerHTML={{ __html: lead }} />

        <div className="pe-h2">Por qué Habitia</div>
        <ul className="pe-benefits">
          {beneficios.map((b, i) => (
            <li key={i}><b>{b.t}</b> {b.d}</li>
          ))}
        </ul>

        <div className="pe-wa">
          <b>¿Vecinos que no usan apps?</b> No hay problema: todo esto también funciona por <b>WhatsApp</b> — consultan su saldo, avisan una incidencia y reciben los avisos, sin instalar nada.
        </div>

        <div className="pe-price">
          <div className="pe-price-main">
            <span className="pe-price-label">Honorario mensual</span>
            <span className="pe-price-value">{honorario}</span>
            <span className="pe-price-iva">IVA incluido</span>
          </div>
          <div className="pe-price-sub">Sin costo de alta ni permanencia.</div>
          <div className="pe-price-incluye"><b>Incluye:</b> {incluye}</div>
        </div>
      </main>

      <footer className="pe-cta">
        <div className="pe-cta-text">
          <div className="pe-cta-title">{ctaTitle}</div>
          {comercial ? <div className="pe-cta-contact">{comercial} · Habitia</div> : null}
          {wa ? <div className="pe-cta-phone">WhatsApp {wa}</div> : null}
          {email ? <div className="pe-cta-email">{email}</div> : null}
          {web ? <div className="pe-cta-web">{web}</div> : null}
        </div>
        {qrDataUrl ? (
          <div className="pe-cta-qr">
            <img src={qrDataUrl} alt="QR WhatsApp" />
            <span>Escaneá y escribime</span>
          </div>
        ) : null}
      </footer>
      <div className="pe-foot">
        {nro ? `Propuesta N° ${nro} · ` : ""}{fecha ? `${fecha} · ` : ""}{web}
      </div>
    </div>
  );
});

export default PresupuestoExpressTemplate;
